import React, { useEffect, useRef, useState, useCallback, useLayoutEffect } from 'react';
import { debounce } from 'lodash';
import { Button } from 'rizzui';
import ModalForm from '../../../components/forms/ModalForm';
import { toast } from 'sonner';
import { PickupService } from '../../../services/pickup/pickupService';

type SignPickupModalProps = {
    isOpen: boolean;
    onClose: () => void;
    pickupId: string;
    onSigned?: (data: { invoiceUrl?: string; ticketUrl?: string }) => void;
};

const HEIGHT_RATIO = 0.36; // ~220/600, alto proporcional al ancho
const MIN_HEIGHT = 180;    // alto mínimo en móviles pequeños

const SignPickupModal: React.FC<SignPickupModalProps> = ({ isOpen, onClose, pickupId, onSigned }) => {
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
    const drawingRef = useRef(false);
    const [busy, setBusy] = useState(false);
    const [hasStroke, setHasStroke] = useState(false);
    const [successData, setSuccessData] = useState<{ invoiceUrl?: string; ticketUrl?: string } | null>(null);

    const serviceRef = useRef(new PickupService());


    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES DESCARGA EL RECIBO DE UNA RECOGIDA
     * @EN DOWNLOADS THE RECEIPT OF A PICKUP
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleDownloadReceipt = async (pickupId: string) => {
        try {
            const response = await (new PickupService()).generateReceipt(pickupId);
            const fileData = response.getResponseData();
            const blob = new Blob([fileData], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `recibo_recogida.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            toast.success('Recibo descargado con éxito');
        } catch (error: any) {
            toast.error(error.message || 'Error al descargar el recibo');
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES DESCARGA EL TICKET DE UNA RECOGIDA
     * @EN DOWNLOADS THE TICKET OF A PICKUP
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleDownloadTicket = async (pickupId: string) => {
        try {
            const response = await (new PickupService()).generateTicket(pickupId);
            const fileData = response.getResponseData();
            const blob = new Blob([fileData], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `ticket_recogida.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            toast.success('Ticket descargado con éxito');
        } catch (error: any) {
            toast.error(error.message || 'Error al descargar el ticket');
        }
    };
    //-------------------------------------------------------------------------------------------------------------


    // ---- tamaño responsivo del canvas
    const resizeCanvas = useCallback(() => {
        const canvas = canvasRef.current;
        const wrapper = wrapperRef.current;
        if (!canvas || !wrapper) {
            console.warn('⚠️ [SignCanvas] Canvas o wrapper no disponible para resize');
            return;
        }

        const wrapperWidth = Math.max(1, Math.floor(wrapper.clientWidth)); // ancho CSS disponible
        const desiredWidth = wrapperWidth; // ocupar todo el ancho
        const desiredHeight = Math.max(MIN_HEIGHT, Math.round(desiredWidth * HEIGHT_RATIO)); // alto proporcional

        const ratio = Math.max(window.devicePixelRatio || 1, 1);


        // tamaño en píxeles reales (para nitidez)
        canvas.width = desiredWidth * ratio;
        canvas.height = desiredHeight * ratio;

        // tamaño visual en CSS
        canvas.style.width = `${desiredWidth}px`;
        canvas.style.height = `${desiredHeight}px`;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
            console.error('❌ [SignCanvas] No se pudo obtener contexto 2D');
            return;
        }

        // reset transform antes de scale (evita acumulaciones)
        // @ts-ignore
        if (ctx.resetTransform) ctx.resetTransform();
        ctx.scale(ratio, ratio);

        // estilo de trazo y fondo
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.lineWidth = 2.5;
        ctx.strokeStyle = '#111827';
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, desiredWidth, desiredHeight);

        ctxRef.current = ctx;
        setHasStroke(false);
        
    }, []);

    // Crear función debounced para redimensionamiento
    const debouncedResize = useCallback(
        debounce(() => {
            if (isOpen) {
                resizeCanvas();
            }
        }, 150), // 150ms de delay para evitar múltiples llamadas
        [isOpen, resizeCanvas]
    );

    // init cuando se abre y ante orient/resize
    useLayoutEffect(() => {
        if (!isOpen) return;

        
        // Pequeño delay para asegurar que el DOM esté listo
        const initTimer = setTimeout(() => {
            resizeCanvas();
        }, 50);

        // Usar ResizeObserver con debounce
        const ro = new ResizeObserver(debouncedResize);
        if (wrapperRef.current) {
            ro.observe(wrapperRef.current);
        }

        // Eventos de ventana también con debounce
        window.addEventListener('orientationchange', debouncedResize, { passive: true });
        window.addEventListener('resize', debouncedResize, { passive: true });

        return () => {
            clearTimeout(initTimer);
            ro.disconnect();
            window.removeEventListener('orientationchange', debouncedResize);
            window.removeEventListener('resize', debouncedResize);
            // Cancelar cualquier debounce pendiente
            debouncedResize.cancel();
        };
    }, [isOpen, resizeCanvas, debouncedResize]);

    // ---- dibujo
    const getPos = (e: React.PointerEvent<HTMLCanvasElement>) => {
        const rect = (e.target as HTMLCanvasElement).getBoundingClientRect();
        return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
        if (!ctxRef.current) {
            console.warn('⚠️ [SignCanvas] Canvas context no disponible en pointerDown');
            return;
        }
        

        drawingRef.current = true;
        const { x, y } = getPos(e);
        ctxRef.current.beginPath();
        ctxRef.current.moveTo(x, y);
        
        try {
            (e.target as HTMLCanvasElement).setPointerCapture(e.pointerId);
        } catch (error) {
            console.warn('⚠️ [SignCanvas] Error al capturar pointer:', error);
        }
    };

    const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
        if (!drawingRef.current || !ctxRef.current) return;
        
        const { x, y } = getPos(e);
        ctxRef.current.lineTo(x, y);
        ctxRef.current.stroke();
        setHasStroke(true);
    };

    const handlePointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
        drawingRef.current = false;
        
        try { 
            (e.target as HTMLCanvasElement).releasePointerCapture(e.pointerId); 
        } catch (error) {
            console.warn('⚠️ [SignCanvas] Error al liberar pointer capture:', error);
        }
    };

    // Funciones de respaldo para touch events (compatibilidad móvil)
    const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
        e.preventDefault();
        if (!ctxRef.current) {
            return;
        }
        
        drawingRef.current = true;
        const touch = e.touches[0];
        const rect = (e.target as HTMLCanvasElement).getBoundingClientRect();
        const x = touch.clientX - rect.left;
        const y = touch.clientY - rect.top;
        
        ctxRef.current.beginPath();
        ctxRef.current.moveTo(x, y);
    };

    const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
        e.preventDefault();
        if (!drawingRef.current || !ctxRef.current) return;
        
        const touch = e.touches[0];
        const rect = (e.target as HTMLCanvasElement).getBoundingClientRect();
        const x = touch.clientX - rect.left;
        const y = touch.clientY - rect.top;
        
        ctxRef.current.lineTo(x, y);
        ctxRef.current.stroke();
        setHasStroke(true);
    };

    const handleTouchEnd = (e: React.TouchEvent<HTMLCanvasElement>) => {
        e.preventDefault();

        drawingRef.current = false;
    };

    const clearSignature = () => {
        const canvas = canvasRef.current;
        const ctx = ctxRef.current;
        if (!canvas || !ctx) return;
        ctx.fillStyle = '#ffffff';
        // usamos el tamaño CSS actual para limpiar en coords no escaladas
        const w = parseInt(canvas.style.width || '0', 10) || 0;
        const h = parseInt(canvas.style.height || '0', 10) || 0;
        ctx.fillRect(0, 0, w, h);
        ctx.fillStyle = '#111827';
        setHasStroke(false);
    };

    const canvasToFile = (): Promise<File> => new Promise((resolve, reject) => {
        const canvas = canvasRef.current;
        if (!canvas) return reject(new Error('No canvas'));
        canvas.toBlob((blob) => {
            if (!blob) return reject(new Error('No se pudo generar la imagen de la firma'));
            resolve(new File([blob], `signature_${pickupId}.png`, { type: 'image/png' }));
        }, 'image/png', 0.95);
    });

    const submitSignature = useCallback(async () => {
        if (!pickupId) {
            toast.error('Falta el ID de la recogida');
            return;
        }
        if (!hasStroke) {
            toast.error('Debes dibujar la firma antes de enviar');
            return;
        }
        setBusy(true);
        try {
            const file = await canvasToFile();
            const svc = serviceRef.current;
            const res = await svc.signReceipt(pickupId, file);

            // ajusta a lo que devuelva tu backend
            const data = typeof (res as any)?.getResponseData === 'function'
                ? (res as any).getResponseData()
                : (res as any)?.response?.data ?? {};

            setSuccessData({ invoiceUrl: data?.invoiceUrl, ticketUrl: data?.ticketUrl });
            onSigned?.({ invoiceUrl: data?.invoiceUrl, ticketUrl: data?.ticketUrl });
            toast.success('Firma enviada correctamente');
        } catch (e: any) {
            console.error(e);
            toast.error(e?.message || 'No se pudo enviar la firma');
        } finally {
            setBusy(false);
        }
    }, [pickupId, hasStroke, onSigned]);

    const closeAll = () => {
        setSuccessData(null);
        onClose();
    };

    // Create useEffect to reset state when modal is closed and cleanup when component unmounts
    useEffect(() => {
        if (!isOpen) {
            // Reset states when modal closes
            setHasStroke(false);
            setBusy(false);
            setSuccessData(null);
            drawingRef.current = false;
            
            // Clear canvas context reference
            ctxRef.current = null;
        }
        
        return () => {
            if (canvasRef.current) {
                const ctx = canvasRef.current.getContext('2d');
                if (ctx) {
                    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
                }
            }
            ctxRef.current = null;
            drawingRef.current = false;
        };
    }, [isOpen]);

    return (
        <ModalForm
            isOpen={isOpen}
            onClose={closeAll}
            title="Firmar recogida"
            containerClassName="pb-5"
        >
            <div className="space-y-4 max-h-[70vh] ">
                {!successData ? (
                    <>
                        <p className="text-sm text-gray-600">
                            Firma con el dedo o el ratón dentro del recuadro.
                        </p>

                        {/* Wrapper que define el ancho disponible */}
                        <div ref={wrapperRef} className="rounded-md border bg-white p-2">
                            <canvas
                                ref={canvasRef}
                                className="block w-full h-auto touch-none"
                                onPointerDown={handlePointerDown}
                                onPointerMove={handlePointerMove}
                                onPointerUp={handlePointerUp}
                                onPointerCancel={handlePointerUp}
                                onTouchStart={handleTouchStart}
                                onTouchMove={handleTouchMove}
                                onTouchEnd={handleTouchEnd}
                                onTouchCancel={handleTouchEnd}
                                style={{ 
                                    touchAction: 'none',
                                    userSelect: 'none',
                                    WebkitUserSelect: 'none',
                                    MozUserSelect: 'none',
                                    msUserSelect: 'none'
                                }}
                            />
                        </div>

                        <div className="flex items-center justify-between">
                            <Button variant="outline" onClick={clearSignature}>
                                Limpiar
                            </Button>
                            <Button onClick={submitSignature} isLoading={busy} disabled={busy}>
                                Enviar firma
                            </Button>
                        </div>
                    </>
                ) : (
                    <>
                        <p className="text-sm text-gray-700">
                            La firma se ha guardado correctamente.
                        </p>
                        <div className="flex gap-3 flex-wrap">
                            <Button
                                onClick={() => handleDownloadReceipt(pickupId)}
                                rel="noreferrer"
                                variant="outline"
                            >
                                Descargar proforma
                            </Button>
                            <Button
                                onClick={() => handleDownloadTicket(pickupId)}
                                rel="noreferrer"
                                variant="outline"
                            >
                                Descargar ticket
                            </Button>
                        </div>
                        <div className="flex justify-end pt-2">
                            <Button onClick={closeAll}>Cerrar</Button>
                        </div>
                    </>
                )}
            </div>
        </ModalForm>
    );
};

export default SignPickupModal;
